import { z } from "zod";
import apiClient from "../../../shared/api/client";

const activeUserSchema = z.object({
  userId: z.string(),
  email: z.string().email(),
  name: z.string(),
  accountType: z.string().nullable(),
  country: z.string().nullable(),
  lastActivityAt: z.string().nullable(),
  // App the account was created from (web | guest | guest-pwa | desktop | mobile | mobile-pwa).
  // Not the same as the tokens below: those are the apps the account USES today. Optional so a
  // backend without the column still parses.
  signupClientApp: z.string().nullable().optional(),
  // Cómo se creó la cuenta: "google", "email", o ausente cuando no hay con qué probarlo.
  // Lo GUARDA el backend al crear la fila (users.signupMethod) y lo sirve tal cual; las
  // cuentas viejas solo se completan desde la auditoría del alta.
  signupMethod: z.string().nullable().optional(),
  // Una entrada por instalación de escritorio: la reporta el shell
  // (X-Eriol-Version / X-Eriol-Platform) y la marca el desinstalador. Es una
  // LISTA porque la misma cuenta puede tener Eriol en la PC y en la Mac, y
  // desinstalar una no dice nada de la otra.
  //
  // El default vacío no es compatibilidad ida y vuelta: es que un backend sin
  // el campo no tire abajo la tabla entera de usuarios por una columna.
  desktopInstalls: z
    .array(
      z.object({
        platform: z.string(),
        version: z.string().nullable(),
        seenAt: z.string().nullable(),
        uninstalledAt: z.string().nullable(),
        // La versión DEL AVISO, que no siempre es `version` (ver
        // DesktopVersionCell). Opcional porque un backend anterior no la manda;
        // y declarada porque `z.object` DESCARTA lo que no está listado: sin
        // esta línea el campo llegaba de la API y moría acá, y la celda
        // mostraba la plataforma sola sin que nada fallara.
        uninstalledVersion: z.string().nullable().optional(),
      }),
    )
    .optional()
    .default([]),
  tokens: z.array(
    z.object({
      id: z.string(),
      issuedAt: z.string(),
      expiresAt: z.string().nullable(),
      active: z.boolean(),
      clientType: z.string().nullable().optional(),
      userAgent: z.string().nullable().optional(),
      ipAddress: z.string().nullable().optional(),
    })
  ),
});

export type ActiveUser = z.infer<typeof activeUserSchema>;

const activeUsersResponseSchema = z.object({
  data: z.array(activeUserSchema),
  meta: z.object({
    total: z.number(),
    refreshedAt: z.string(),
  }),
});

export type ActiveUsersResponse = z.infer<typeof activeUsersResponseSchema>;

export const fetchActiveUsers = async (): Promise<ActiveUsersResponse> => {
  const response = await apiClient.get("/auth/active-users");
  return activeUsersResponseSchema.parse(response.data);
};

export const revokeUserToken = async (tokenId: string) => {
  await apiClient.post(`/auth/tokens/${tokenId}/revoke`);
};

export const revokeUserAllTokens = async (userId: string) => {
  await apiClient.post(`/auth/users/${userId}/revoke`);
};
